import { Box, Button, Menu, MenuItem, Typography } from '@mui/material';
import React, { FC, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch } from 'store/store';
import { getActiveTicket, getIsTicketUpdate } from 'store/ticket-service/selector';
import {
	setActiveHistoryItem,
	setHistoryMode,
	setIsTariffPlansModalOpen,
	setPaymentMode,
	setReportAnIssueMode,
	setSidebarView,
	setUpdatePaymentInfoModal,
} from 'store/ticket-service/ticketSlice';
import { setIsCreateTicketShareOpen } from 'store/ticket-share-service/ticketShareSlice';
import { getActiveSubscription, getAllPaymentMethods } from 'store/user-service/asyncActions';
import {
	getCurrentSubscription,
	getIsOpenUserProfile,
	getPaymentMethods,
	getUserBalance,
} from 'store/user-service/selector';
import { setCurrentSubscription, setIsOpenUserProfile } from 'store/user-service/userSlice';

import { flexMixin } from 'utils/flexMixin';
import { getLocalStorageBalance } from 'utils/localStorageHelpers/balanceHelpers';

import { getEnvVarAsBoolean } from 'core/helpers/env-helper';
import { ROUTES } from 'core/constants/routes';
import { NamespacesList } from 'core/enums/common';
import { useGetUser } from 'core/hooks/useGetUser';
import { PaymentMethodType } from 'core/types/base';

import { CreateTicketModal } from 'components/Ticket/CreateTicketModal';
import { CustomActionTooltip } from 'components/shared/CustomActionTooltip';
import { CustomTooltipElement } from 'components/shared/CustomDisabledElement/CustomTooltipElement';
import { LanguageSelect } from 'components/shared/CustomSelect/LanguageSelect';

import { ReactComponent as TokensIcon } from 'assets/icons/coins.svg';
import { ReactComponent as HomeTab } from 'assets/icons/home-tab.svg';
import { ReactComponent as Logo } from 'assets/icons/logo-ellogy.svg';
import { ReactComponent as Logout } from 'assets/icons/logout.svg';
import { ReactComponent as MessageQuestion } from 'assets/icons/message-question.svg';
import { ReactComponent as NotificationsTab } from 'assets/icons/notifications-tab.svg';
import { ReactComponent as ProjectsTab } from 'assets/icons/projects-tab.svg';
import { ReactComponent as RequestsTab } from 'assets/icons/requests-tab.svg';
import { ReactComponent as SettingsTab } from 'assets/icons/settings-tab.svg';
import { ReactComponent as SupportTab } from 'assets/icons/support-tab.svg';

import { CustomAvatar } from '../CustomAvatar/CustomAvatar';
import { LogoutModal } from '../LogoutModal/LogoutModal';

import * as Styled from './styled';

interface HeaderProps {
	isCreating?: boolean;
}

interface ISidebarItem {
	titlePath: string;
	icon: ReactElement;
	selected: boolean;
	isInDevelopment: boolean;
	onClick: () => any;
}

export const Header: FC<HeaderProps> = ({ isCreating }) => {
	const { i18n } = useTranslation();
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const isPaymentEnabled = getEnvVarAsBoolean('REACT_APP_ENABLE_PAYMENTS', true);

	const activeTicket = useSelector(getActiveTicket);
	const isTicketUpdate = useSelector(getIsTicketUpdate);
	const userBalance = useSelector(getUserBalance);
	const currentSubscription = useSelector(getCurrentSubscription);
	const isUserProfileOpen = useSelector(getIsOpenUserProfile);
	const paymentMethods = useSelector(getPaymentMethods);

	const handleCloseUserProfile = () => {
		if (isUserProfileOpen) dispatch(setIsOpenUserProfile(false));
	};

	const handleReportAnIssue = () => {
		dispatch(setReportAnIssueMode(false));
		dispatch(setHistoryMode(false));
		dispatch(setActiveHistoryItem(null));
		dispatch(setIsOpenUserProfile(false));
		dispatch(setIsCreateTicketShareOpen(false));
		if (activeTicket) dispatch(setReportAnIssueMode(true));
		navigate(ROUTES.SUPPORT);
	};

	const handleOpenProfile = () => {
		if (isTicketUpdate) {
			setIsShowDraft && setIsShowDraft(true);
		} else {
			dispatch(setReportAnIssueMode(false));
			dispatch(setHistoryMode(false));
			dispatch(setActiveHistoryItem(null));
			dispatch(setIsOpenUserProfile(true));
			dispatch(setIsCreateTicketShareOpen(false));
			// Naviguer vers /settings/profile par défaut
			navigate(ROUTES.SETTINGS_PROFILE);
		}
	};

	const handleOpenRequests = () => {
		dispatch(setReportAnIssueMode(false));
		dispatch(setHistoryMode(false));
		dispatch(setActiveHistoryItem(null));
		dispatch(setIsOpenUserProfile(false));
		dispatch(setIsCreateTicketShareOpen(false));
		// Ouvrir la sidebar sur mobile quand on navigue vers BRD
		const isMobile = window.matchMedia('(max-width: 899px)').matches;
		if (isMobile) {
			dispatch(setSidebarView(true));
		}
		navigate(ROUTES.BRD);
	};

	const handleOpenHome = () => {
		dispatch(setReportAnIssueMode(false));
		dispatch(setHistoryMode(false));
		dispatch(setActiveHistoryItem(null));
		dispatch(setIsOpenUserProfile(false));
		dispatch(setIsCreateTicketShareOpen(false));
		navigate(ROUTES.DASHBOARD);
	};

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
	const [isContentOverflowing, setIsContentOverflowing] = useState(false);
	const [isShowDraft, setIsShowDraft] = useState(false);
	const [cachedBalance, setCachedBalance] = useState(0);
	const [sidebarTabsConfig, setSidebarTabsConfig] = useState<Array<ISidebarItem>>([]);

	const { user } = useGetUser();
	const locationObject = useLocation();

	useEffect(() => {
		setCachedBalance(getLocalStorageBalance());
	}, []);

	useEffect(() => {
		const updatedConfig = [
			{
				titlePath: 'sidebarTabs.home',
				icon: <HomeTab />,
				selected: locationObject.pathname === ROUTES.DASHBOARD,
				isInDevelopment: false,
				onClick: handleOpenHome,
			},
			{
				titlePath: 'sidebarTabs.projects',
				icon: <ProjectsTab />,
				selected: locationObject.pathname === ROUTES.PROJECTS,
				isInDevelopment: true,
				onClick: () => null,
			},
			{
				titlePath: 'sidebarTabs.requests',
				icon: <RequestsTab />,
				selected: locationObject.pathname === ROUTES.BRD,
				isInDevelopment: false,
				onClick: handleOpenRequests,
			},
			{
				titlePath: 'sidebarTabs.notifications',
				icon: <NotificationsTab />,
				selected: locationObject.pathname === ROUTES.NOTIFICATIONS,
				isInDevelopment: true,
				onClick: () => null,
			},
			{
				titlePath: 'sidebarTabs.settings',
				icon: <SettingsTab />,
				selected: locationObject.pathname === ROUTES.SETTINGS || 
					locationObject.pathname === ROUTES.SETTINGS_PROFILE || 
					locationObject.pathname === ROUTES.SETTINGS_SUBSCRIPTION ||
					locationObject.pathname === ROUTES.SETTINGS_DOCS ||
					locationObject.pathname === ROUTES.SETTINGS_MODEL,
				isInDevelopment: false,
				onClick: handleOpenProfile,
			},
			{
				titlePath: 'sidebarTabs.support',
				icon: <SupportTab />,
				selected: locationObject.pathname === ROUTES.SUPPORT,
				isInDevelopment: false,
				onClick: handleReportAnIssue,
			},
		];
		setSidebarTabsConfig(updatedConfig);
	}, [locationObject.pathname]);

	const userBillingCheck = useMemo(() => {
		return (
			user?.stripeCustomerId === null ||
			user?.stripeCustomerId === '' ||
			paymentMethods?.length === 0 ||
			!paymentMethods?.find((item: PaymentMethodType) => item.default)
		);
	}, [user?.stripeCustomerId, paymentMethods]);

	const open = useMemo(() => Boolean(anchorEl), [anchorEl]);

	const handleClickOnMenu = (event: React.MouseEvent<HTMLDivElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setTimeout(() => {
			setAnchorEl(null);
		}, 1);
	};

	const handleSidebarTabClick = (selectedTab: ISidebarItem) => {
		// Ne pas mettre à jour le highlight manuellement ici
		// Le useEffect qui écoute locationObject.pathname s'en charge automatiquement
		// quand la navigation réussit (après confirmation dans la popup si nécessaire)
		switch (selectedTab.titlePath) {
			case 'sidebarTabs.home':
				handleOpenHome();
				break;
			case 'sidebarTabs.support':
				handleReportAnIssue();
				break;
			case 'sidebarTabs.settings':
				handleOpenProfile();
				break;
			case 'sidebarTabs.requests':
				handleOpenRequests();
				break;
			default:
				return;
		}
	};

	const handleGetAllPaymentsMethods = async () => {
		try {
			await dispatch(getAllPaymentMethods({ recordsPerPage: 5 })).unwrap();
		} catch (error) {
			console.error('getAllPaymentMethods error', error);
		}
	};

	const handleGetCurrentSubscriptionLevel = async () => {
		if (user && getEnvVarAsBoolean('REACT_APP_ENABLE_PAYMENTS', true)) {
			const activeTariff = await dispatch(getActiveSubscription()).unwrap();

			if (activeTariff) dispatch(setCurrentSubscription(activeTariff));
		}
	};

	const logout = () => {
		if (isTicketUpdate) {
			setIsShowDraft && setIsShowDraft(true);
		}
		!isShowDraft && setIsLogoutModalOpen(true);
		setAnchorEl(null);
	};

	const handleProfileAction = () => {
		dispatch(setReportAnIssueMode(false));
		dispatch(setHistoryMode(false));
		dispatch(setActiveHistoryItem(null));
		dispatch(setIsOpenUserProfile(true));
		handleClose();
	};

	const handleCloseCreateTicketModal = () => {
		if (setIsShowDraft) {
			setIsShowDraft(false);
		}
	};

	const handleActionComplete = () => {
		handleProfileAction();
		navigate(ROUTES.SETTINGS_PROFILE);
	};

	const handleCloseLogoutModal = () => {
		setIsLogoutModalOpen(false);
	};

	const firstNameRef = useRef<HTMLDivElement>(null);
	const lastNameRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const contentWidth =
			(firstNameRef.current?.offsetWidth || 0) + (lastNameRef.current?.offsetWidth || 0) > 128;
		setIsContentOverflowing(contentWidth);
	}, []);

	useEffect(() => {
		const storedLang = localStorage.getItem('i18nextLng');
		if (storedLang !== 'en' && storedLang !== 'ar') {
			window.localStorage.setItem('i18nextLng', 'en');
		}
		if (i18n.language !== 'en' && i18n.language !== 'ar') {
			i18n.changeLanguage('en');
		}
	}, [i18n]);

	useEffect(() => {
		if (user && user?.stripeCustomerId && user?.stripeCustomerId.length > 0) {
			handleGetAllPaymentsMethods();
		}
	}, [user]);

	useEffect(() => {
		if (!currentSubscription) handleGetCurrentSubscriptionLevel();
	}, [currentSubscription]);

	const isLoginPage = useMemo(
		() =>
			locationObject.pathname === ROUTES.LOGIN ||
			locationObject.pathname === ROUTES.SIGNUP ||
			locationObject.pathname === ROUTES.RESET_PASSWORD ||
			locationObject.pathname === ROUTES.CREATE_NEW_PASSWORD,
		[locationObject.pathname]
	);

	const handlePaymentClick = () => {
		if (!userBillingCheck) {
			const localStorage = window.localStorage;

			if (localStorage?.paymentModalOpenings === undefined)
				localStorage.setItem('paymentModalOpenings', JSON.stringify({}));

			const paymentModalOpenings = JSON.parse(localStorage.paymentModalOpenings);

			if (user && paymentModalOpenings?.[user.id] === undefined) {
				localStorage.setItem(
					'paymentModalOpenings',
					JSON.stringify({ ...paymentModalOpenings, [user.id]: 1 })
				);
				dispatch(setPaymentMode(true));
			} else if (user) {
				if (paymentModalOpenings?.[user.id] < 2) {
					localStorage.setItem(
						'paymentModalOpenings',
						JSON.stringify({
							...paymentModalOpenings,
							[user.id]: paymentModalOpenings?.[user.id] + 1,
						})
					);

					dispatch(setPaymentMode(true));
				} else {
					dispatch(setIsTariffPlansModalOpen(true));
				}
			}
		} else {
			dispatch(
				setUpdatePaymentInfoModal({
					isOpen: true,
					paymentMethods: paymentMethods,
					previousUrlQuery: isUserProfileOpen ? 'isProfile=true' : '',
				})
			);
		}
	};

	const renderButton = () => {
		if (locationObject.pathname === ROUTES.LOGIN) {
			return (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: '20px',
					}}
				>
					<LanguageSelect />
					<Button
						component={Link}
						to="/sign-up"
						sx={{
							width: '150px',
							height: '45px',
							border: '1px solid #4786FF',
							borderRadius: '8px',
							textTransform: 'none',
							fontWeight: '700',
						}}
					>
						{commonTranslate('buttons.signUp')}
					</Button>
				</Box>
			);
		} else if (
			locationObject.pathname === ROUTES.SIGNUP ||
			locationObject.pathname === ROUTES.RESET_PASSWORD ||
			locationObject.pathname === ROUTES.CREATE_NEW_PASSWORD
		) {
			return (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: '20px',
					}}
				>
					<LanguageSelect />
					<Button
						component={Link}
						to="/sign-in"
						sx={{
							width: '150px',
							height: '45px',
							border: '1px solid #4786FF',
							borderRadius: '8px',
							textTransform: 'none',
							fontWeight: '700',
						}}
					>
						{commonTranslate('buttons.logIn')}
					</Button>
				</Box>
			);
		}

		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: { xs: '4px', sm: '6px', md: '8px' },
					marginTop: { xs: '8px', sm: '12px', md: '16px' },
				}}
			>
				<Styled.SidebarItemsList className="sidebar-menu">
							{sidebarTabsConfig.map((sidebarItem, index) =>
								sidebarItem.isInDevelopment ? (
									<CustomTooltipElement
										key={`${sidebarItem.titlePath}.${index}`}
										message={commonTranslate('text.inDevelopment')}
									>
										<Styled.SidebarItemContainer
											key={sidebarItem.titlePath}
											selected={sidebarItem.selected}
											disabled={true}
											onClick={() => handleSidebarTabClick(sidebarItem)}
										>
											<Box
												sx={{
													'& svg': {
														width: { xs: '18px', sm: '20px', md: '24px' },
														height: { xs: '18px', sm: '20px', md: '24px' },
													},
												}}
											>
												{sidebarItem.icon}
											</Box>
											<Styled.SidebarItemText>
												{commonTranslate(sidebarItem.titlePath)}
											</Styled.SidebarItemText>
										</Styled.SidebarItemContainer>
									</CustomTooltipElement>
								) : (
									<Styled.SidebarItemContainer
										key={sidebarItem.titlePath}
										selected={sidebarItem.selected}
										onClick={() => handleSidebarTabClick(sidebarItem)}
									>
										<Box
											sx={{
												'& svg': {
													width: { xs: '18px', sm: '20px', md: '24px' },
													height: { xs: '18px', sm: '20px', md: '24px' },
												},
											}}
										>
											{sidebarItem.icon}
										</Box>
										<Styled.SidebarItemText>
											{commonTranslate(sidebarItem.titlePath)}
										</Styled.SidebarItemText>
									</Styled.SidebarItemContainer>
								)
							)}
				</Styled.SidebarItemsList>
			</Box>
		);
	};

	return (
		<Box
			sx={{
			boxSizing: 'border-box',
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'space-between',
			width: 'clamp(80px, 13%, 90px)',
			minWidth: '80px',
			height: '100%',
			padding: '12px 0',
			borderBottom: '1px solid #E7E8EC',
			background: '#4786FF',
			boxShadow: '0px 4px 16px rgba(40, 103, 131, 0.06)',
			borderRadius: '8px 0 0 8px',
				alignSelf: 'stretch',
				overflow: 'hidden',
				flexShrink: 0,
			}}
		>
			{/* Partie scrollable: Logo, tokens et menu */}
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					flex: 1,
					overflowY: 'auto',
					overflowX: 'hidden',
					width: '100%',
					'&::-webkit-scrollbar': {
						width: '4px',
					},
					'&::-webkit-scrollbar-track': {
						background: 'transparent',
					},
					'&::-webkit-scrollbar-thumb': {
						background: 'rgba(255, 255, 255, 0.3)',
						borderRadius: '4px',
					},
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						gap: { xs: '12px', sm: '16px', md: '24px' },
						marginBottom: { xs: '12px', sm: '16px', md: '24px' },
					}}
				>
					<Box
						sx={{
							width: { xs: '40px', sm: '50px', md: 'auto' },
							height: { xs: '40px', sm: '50px', md: 'auto' },
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							'& svg': {
								width: { xs: '40px', sm: '50px', md: '100%' },
								height: { xs: '40px', sm: '50px', md: 'auto' },
							},
						}}
					>
						<Logo />
					</Box>

					{isPaymentEnabled && !isLoginPage ? (
						<Styled.PaymentContainer>
							<Styled.PaymentButtonContainer className="rtl-able">
								<Styled.PaymentText>{userBalance || cachedBalance}</Styled.PaymentText>
								<CustomActionTooltip
									tooltipMessage={commonTranslate('tooltips.addDefaultPaymentMethodTooltip')}
									check={userBillingCheck}
								>
									<TokensIcon style={{ height: '16px', width: '16px' }} />
								</CustomActionTooltip>
							</Styled.PaymentButtonContainer>
							<Styled.BuyButton variant="outlined" color="secondary" onClick={handlePaymentClick}>
								<Styled.PaymentText>{commonTranslate('buttons.purchase')}</Styled.PaymentText>
							</Styled.BuyButton>
						</Styled.PaymentContainer>
					) : null}

					{renderButton()}
				</Box>

				<Button
					sx={{
						display: {
							xs: isLoginPage ? 'flex' : 'none',
							md: 'none',
						},
						height: { xs: '36px', sm: '44px' },
						padding: '0',
						minWidth: { xs: '36px', sm: '44px' },
						width: { xs: '36px', sm: '44px' },
						borderRadius: '8px',
						textTransform: 'none',
						fontSize: { xs: '14px', sm: '16px' },
						fontWeight: '700',
						gap: { xs: '6px', sm: '8px' },
						'& svg': {
							width: { xs: '16px', sm: '20px' },
							height: { xs: '16px', sm: '20px' },
						},
					}}
					variant="outlined"
					color="primary"
					onClick={() => {
						activeTicket && dispatch(setReportAnIssueMode(true));
						activeTicket && dispatch(setSidebarView(false));
					}}
				>
					<MessageQuestion />
				</Button>
			</Box>

			{/* Partie fixe en bas: Langue et Avatar */}
			<Box 
				sx={{ 
					display: 'flex', 
					flexDirection: 'column', 
					alignItems: 'center',
					gap: { xs: '8px', sm: '10px', md: '12px' },
					flexShrink: 0,
					paddingTop: { xs: '8px', sm: '10px', md: '12px' },
				}}
			>
				<LanguageSelect />

				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						gap: { xs: '1px', sm: '1px' },
						maxWidth: { xs: '100%', sm: '222px' },
						width: isContentOverflowing ? 'min-content' : 'auto',
						cursor: 'pointer',
					}}
					onClick={(event) => handleClickOnMenu(event)}
				>
					<Box
						sx={{
							'& .MuiAvatar-root': {
								width: { xs: '32px', sm: '40px' },
								height: { xs: '32px', sm: '40px' },
								fontSize: { xs: '14px', sm: '16px' },
							},
						}}
					>
						<CustomAvatar />
					</Box>

					<Styled.ProfileInfoText>{user?.firstName}</Styled.ProfileInfoText>

					<Menu
						className="rtl-able"
						data-testid="anchor-element"
						PaperProps={{
							style: {
								width: 212,
								marginTop: '12px',
							},
						}}
						anchorEl={anchorEl}
						anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
						transformOrigin={{ vertical: 'top', horizontal: 'right' }}
						open={open}
						onClose={handleClose}
					>
						<MenuItem onClick={logout}>
							<Box
								sx={{
									...flexMixin({ gap: 12 }),
								}}
							>
								<Logout />
								<Typography sx={{ color: '#FB0B24' }}>
									{commonTranslate('buttons.logOut')}
								</Typography>
							</Box>
						</MenuItem>
					</Menu>
				</Box>
			</Box>
			{isShowDraft && activeTicket && (
				<CreateTicketModal
					handleCloseModal={handleCloseCreateTicketModal}
					ticket={activeTicket}
					userId={user?.id || ''}
					isCreating={isCreating}
					onActionComplete={handleActionComplete}
				/>
			)}
			{isLogoutModalOpen && !isShowDraft && (
				<LogoutModal handleCloseModal={handleCloseLogoutModal} />
			)}
		</Box>
	);
};
